import { type FormEvent, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import TokenManager from "../../commons/TokenManager";
import { getAuthenticatedRedirectUrl } from "../../commons/helpers";
import { useAuthContext } from "../../contexts/Auth";
import useUsers from "../../services/useUsers";

const theme = createTheme();

const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

function getNewPasswordError(value: string) {
    if (!passwordRegex.test(value)) {
        return "A senha deve ter pelo menos 8 caracteres, uma letra e um numero.";
    }
    return "";
}

function getConfirmPasswordError(password: string, confirmPassword: string) {
    if (password !== confirmPassword) {
        return "As senhas nao conferem.";
    }
    return "";
}

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { resetUserPassword } = useUsers();
    const { isAuthenticated } = useAuthContext();

    const [token, setToken] = useState("");
    const [tokenChecked, setTokenChecked] = useState(false);
    const [isTokenMissing, setIsTokenMissing] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        const tokenFromUrl = searchParams.get("token");
        if (tokenFromUrl) {
            setToken(tokenFromUrl);
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("token");
            setSearchParams(nextParams, { replace: true });
            setIsTokenMissing(false);
        } else {
            setIsTokenMissing(true);
        }
        setTokenChecked(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const newPasswordError = getNewPasswordError(newPassword);
    const confirmPasswordError = getConfirmPasswordError(newPassword, confirmPassword);

    const showNewPasswordError =
        (submitAttempted || touched.newPassword) && Boolean(newPasswordError);
    const showConfirmPasswordError =
        (submitAttempted || touched.confirmPassword) && Boolean(confirmPasswordError);

    useEffect(() => {
        if (isAuthenticated) {
            const tokenData = TokenManager.getTokenData();
            window.location.replace(getAuthenticatedRedirectUrl(Boolean(tokenData.validated)));
        }
    }, [isAuthenticated]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);

        if (isTokenMissing) {
            return;
        }

        if (newPasswordError || confirmPasswordError) {
            return;
        }

        const success = await resetUserPassword({
            token,
            newPassword,
        });

        if (success) {
            navigate("/", { replace: true });
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: "blue" }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Redefinir senha
                    </Typography>
                    {tokenChecked && isTokenMissing && (
                        <Typography color="error" sx={{ mt: 1, textAlign: "center" }}>
                            Token invalido ou ausente. Solicite uma nova URL ao administrador.
                        </Typography>
                    )}
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="newPassword"
                            label="nova senha"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={newPassword}
                            disabled={isTokenMissing}
                            onChange={(event) => {
                                setNewPassword(event.target.value);
                            }}
                            onBlur={() => {
                                setTouched((prevState) => ({
                                    ...prevState,
                                    newPassword: true,
                                }));
                            }}
                            error={showNewPasswordError}
                            helperText={showNewPasswordError ? newPasswordError : ""}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => {
                                            setShowNewPassword(!showNewPassword);
                                        }}
                                    >
                                        {showNewPassword ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                ),
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="confirmPassword"
                            label="confirmar nova senha"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirmPassword}
                            disabled={isTokenMissing}
                            onChange={(event) => {
                                setConfirmPassword(event.target.value);
                            }}
                            onBlur={() => {
                                setTouched((prevState) => ({
                                    ...prevState,
                                    confirmPassword: true,
                                }));
                            }}
                            error={showConfirmPasswordError}
                            helperText={showConfirmPasswordError ? confirmPasswordError : ""}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => {
                                            setShowConfirmPassword(!showConfirmPassword);
                                        }}
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        )}
                                    </IconButton>
                                ),
                            }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            type="submit"
                            disabled={isTokenMissing}
                        >
                            Redefinir senha
                        </Button>
                        <Link
                            component={RouterLink}
                            to="/"
                            underline="hover"
                            sx={{ display: "block", textAlign: "center" }}
                        >
                            Voltar para o login
                        </Link>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
}
